import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { EmptyState as EmptyStateBase, type EmptyStateIllustrationKind, type EmptyStateVariant } from '@/components/ui/empty-state'

export function ListSkeleton({ count = 3, className }: { count?: number; className?: string }) {
  const { t } = useTranslation()
  return (
    <div className={cn('space-y-2', className)} aria-busy="true" aria-label={t('common.loading')}>
      {Array.from({ length: count }).map((_, i) => (
        <div
          key={i}
          className="h-[72px] motion-safe:animate-pulse motion-reduce:animate-none rounded-none border border-enterprise-border bg-enterprise-border"
        />
      ))}
    </div>
  )
}

export function ListEmpty({
  title,
  description,
  action,
  variant,
  illustration,
  onClearFilters,
  className,
}: {
  title: string
  description?: string
  action?: React.ReactNode
  variant?: EmptyStateVariant
  illustration?: EmptyStateIllustrationKind
  onClearFilters?: () => void
  className?: string
}) {
  return (
    <EmptyStateBase
      title={title}
      description={description}
      action={action}
      variant={variant ?? 'empty'}
      illustration={illustration ?? 'generic'}
      onClearFilters={onClearFilters}
      className={className}
    />
  )
}

export function ListError({
  message,
  onRetry,
  className,
}: {
  message: string
  onRetry?: () => void
  className?: string
}) {
  const { t } = useTranslation()
  return (
    <div
      role="alert"
      aria-live="polite"
      className={cn(
        'flex min-h-[88px] flex-wrap items-center justify-between gap-3 rounded-none border border-enterprise-danger/20 bg-enterprise-danger/10 px-4 py-3 text-sm shadow-none motion-reduce:transition-none',
        className,
      )}
    >
      <span className="max-w-[60ch] break-words text-xs leading-5 text-enterprise-danger">{message}</span>
      {onRetry ? (
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={onRetry}
          className="min-h-9 shrink-0 rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-enterprise-indigo focus-visible:ring-offset-1 focus-visible:ring-offset-enterprise-bg motion-reduce:transition-none"
        >
          {t('common.retry')}
        </Button>
      ) : null}
    </div>
  )
}

// Re-export unified empty state for direct use
export { EmptyStateBase as EmptyState }
export type { EmptyStateVariant, EmptyStateIllustrationKind }
